import {
  OnboardingDraftV1Schema,
  UsernameSchema,
  normalizeUsername,
  type OnboardingDraftV1,
  type OnboardingInterest,
  type OnboardingSelfDescription,
  type UsernameAvailability,
} from "@repo/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearOnboardingV1Draft,
  createOnboardingV1Draft,
  readOnboardingV1Draft,
  resolveOnboardingV1DraftStep,
  writeOnboardingV1Draft,
  type OnboardingV1DraftStorage,
} from "~/features/start/data/onboarding-v1-draft";
import type { OnboardingV1StepId } from "~/features/start/data/onboarding-v1-step-search";
import { m as messages } from "~/lib/i18n";
import { queryKeys } from "~/lib/query-keys";
import { toast } from "~/lib/toast";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import {
  createOnboardingV1AccountDataSource,
  hasOnboardingV1ProvisioningState,
  type OnboardingV1AccountDataSource,
  type OnboardingV1ClaimedDraft,
} from "./onboarding-v1-account-data-source";
import type { OnboardingV1UsernameIdentity } from "./use-onboarding-v1-username-identity";

export interface OnboardingV1QuestionState {
  readonly interests: readonly OnboardingInterest[];
  readonly interestsUnknown: boolean;
  readonly selfDescriptions: readonly OnboardingSelfDescription[];
  readonly username: string;
}

type UsernameCheckState =
  | "available"
  | "checking"
  | "error"
  | "idle"
  | "invalid"
  | "reserved"
  | "saving"
  | "unavailable";

type EntryAuthMode = "anonymous" | "authenticated" | "resolving";

const USERNAME_AVAILABILITY_DEBOUNCE_MS = 350;

interface OnboardingV1PageControllerOptions {
  readonly accountDataSource?: OnboardingV1AccountDataSource;
  readonly checkUsernameAvailability?: (
    username: string,
  ) => Promise<UsernameAvailability>;
  readonly draftStorage?: OnboardingV1DraftStorage | null;
  readonly onClose: () => void;
  readonly onExplore: () => void;
  readonly onStepChange: (step: OnboardingV1StepId) => void;
  readonly step: OnboardingV1StepId;
  readonly usernameIdentity: OnboardingV1UsernameIdentity;
}

async function loadUsernameAvailability(
  username: string,
): Promise<UsernameAvailability> {
  const platformApi = getBrowserPlatformApiClient();
  return await platformApi.userOnboarding.checkUsernameAvailability.query(
    username,
  );
}

export function useOnboardingV1PageController(
  options: OnboardingV1PageControllerOptions,
) {
  const queryClient = useQueryClient();
  const usernameCheckRequestRef = useRef(0);
  const didResolveInitialDraftRef = useRef(false);
  const usernameIdentity = options.usernameIdentity;
  const [entryAuthMode, setEntryAuthMode] = useState<EntryAuthMode>(() =>
    usernameIdentity.authReady
      ? usernameIdentity.authenticated
        ? "authenticated"
        : "anonymous"
      : "resolving",
  );
  const [accountDataSource] = useState(
    () => options.accountDataSource ?? createOnboardingV1AccountDataSource(),
  );
  const [draft, setDraft] = useState<OnboardingDraftV1 | null>(() =>
    readOnboardingV1Draft(options.draftStorage),
  );
  const draftRef = useRef(draft);
  const [questionState, setQuestionState] = useState<OnboardingV1QuestionState>(
    () => ({
      interests: draft?.interests ?? [],
      interestsUnknown: draft?.interestsUnknown ?? false,
      selfDescriptions: draft?.selfDescriptions ?? [],
      username: draft?.username ?? "",
    }),
  );
  const [usernameCheckState, setUsernameCheckState] =
    useState<UsernameCheckState>("idle");
  const [canonicalUsername, setCanonicalUsername] = useState<string | null>(
    usernameIdentity.currentUsername,
  );
  const [claimedDraft, setClaimedDraft] =
    useState<OnboardingV1ClaimedDraft | null>(null);
  const [claimedInCurrentSession, setClaimedInCurrentSession] = useState(false);
  const [claimedDraftLoadState, setClaimedDraftLoadState] = useState<
    "error" | "idle" | "loaded" | "loading"
  >("idle");
  const [usernameIdentityApplied, setUsernameIdentityApplied] = useState(
    entryAuthMode === "anonymous",
  );
  const [pageError, setPageError] = useState<
    "close" | "identity" | "load" | null
  >(null);
  const [isClosing, setIsClosing] = useState(false);

  const persistDraftPatch = useCallback(
    (patch: Partial<Omit<OnboardingDraftV1, "updatedAt" | "version">>) => {
      const candidate = {
        ...(draftRef.current ?? createOnboardingV1Draft()),
        ...patch,
        updatedAt: new Date().toISOString(),
      };
      const parsed = OnboardingDraftV1Schema.safeParse(candidate);
      if (!parsed.success) {
        return;
      }

      draftRef.current = parsed.data;
      writeOnboardingV1Draft(parsed.data, options.draftStorage);
      setDraft(parsed.data);
    },
    [options.draftStorage],
  );

  useEffect(() => {
    if (entryAuthMode !== "resolving" || !usernameIdentity.authReady) {
      return;
    }
    setEntryAuthMode(
      usernameIdentity.authenticated ? "authenticated" : "anonymous",
    );
    if (!usernameIdentity.authenticated) {
      setUsernameIdentityApplied(true);
    }
  }, [
    entryAuthMode,
    usernameIdentity.authReady,
    usernameIdentity.authenticated,
  ]);

  useEffect(() => {
    if (entryAuthMode !== "authenticated" || usernameIdentityApplied) {
      return;
    }
    if (usernameIdentity.currentUsernameStatus === "error") {
      setPageError("identity");
      return;
    }
    if (usernameIdentity.currentUsernameStatus !== "success") {
      return;
    }

    const currentUsername = usernameIdentity.currentUsername;
    setCanonicalUsername(currentUsername);
    setQuestionState((current) => ({
      ...current,
      username: currentUsername ?? "",
    }));
    if (currentUsername !== null || draft !== null) {
      persistDraftPatch({ username: currentUsername });
    }
    setPageError(null);
    setUsernameIdentityApplied(true);
    if (currentUsername !== null && options.step === "name") {
      didResolveInitialDraftRef.current = true;
      options.onStepChange("profile");
    }
  }, [
    draft,
    entryAuthMode,
    persistDraftPatch,
    options.onStepChange,
    options.step,
    usernameIdentity.currentUsername,
    usernameIdentity.currentUsernameStatus,
    usernameIdentityApplied,
  ]);

  useEffect(() => {
    if (
      entryAuthMode === "authenticated" &&
      usernameIdentityApplied &&
      canonicalUsername !== null &&
      options.step === "name"
    ) {
      options.onStepChange("profile");
    }
  }, [
    canonicalUsername,
    entryAuthMode,
    options.onStepChange,
    options.step,
    usernameIdentityApplied,
  ]);

  useEffect(() => {
    if (didResolveInitialDraftRef.current) {
      return;
    }
    if (entryAuthMode === "resolving" || !usernameIdentityApplied) {
      return;
    }
    const isWaitingForClaimedDraft =
      options.step === "fund" &&
      draft === null &&
      claimedDraft === null &&
      claimedDraftLoadState !== "loaded";
    if (isWaitingForClaimedDraft) {
      return;
    }

    didResolveInitialDraftRef.current = true;

    if (claimedDraft !== null) {
      return;
    }

    const resolvedStep = resolveOnboardingV1DraftStep(options.step, draft);
    if (resolvedStep !== options.step) {
      options.onStepChange(resolvedStep);
    }
  }, [
    claimedDraft,
    claimedDraftLoadState,
    draft,
    entryAuthMode,
    options.onStepChange,
    options.step,
    usernameIdentityApplied,
  ]);

  const loadClaimedDraft = useCallback(async () => {
    setClaimedDraftLoadState("loading");
    setPageError(null);
    try {
      const loadedDraft = await accountDataSource.loadClaimedDraft();
      setClaimedDraft(
        loadedDraft && loadedDraft.onboardingStepId !== null
          ? loadedDraft
          : null,
      );
      setClaimedInCurrentSession(false);
      setClaimedDraftLoadState("loaded");
    } catch {
      setClaimedDraftLoadState("error");
      setPageError("load");
    }
  }, [accountDataSource]);

  useEffect(() => {
    if (
      options.step === "fund" &&
      draft === null &&
      claimedDraft === null &&
      claimedDraftLoadState === "idle"
    ) {
      void loadClaimedDraft();
    }
  }, [
    claimedDraft,
    claimedDraftLoadState,
    draft,
    loadClaimedDraft,
    options.step,
  ]);

  const runUsernameAvailabilityCheck = useCallback(
    async (username: string, requestId: number) => {
      setUsernameCheckState("checking");
      try {
        const availability = options.checkUsernameAvailability
          ? await options.checkUsernameAvailability(username)
          : await loadUsernameAvailability(username);
        if (usernameCheckRequestRef.current !== requestId) {
          return;
        }
        setQuestionState((current) => ({
          ...current,
          username: availability.username,
        }));
        setUsernameCheckState(availability.status);
      } catch {
        if (usernameCheckRequestRef.current !== requestId) {
          return;
        }
        setUsernameCheckState("error");
        toast.error(messages.onboarding_v1_username_check_error(), {
          id: "onboarding-v1-username-check-error",
        });
      }
    },
    [options.checkUsernameAvailability],
  );

  useEffect(() => {
    // Every pass invalidates an older debounce or request, including when the
    // name step is left before that work resolves.
    const requestId = usernameCheckRequestRef.current + 1;
    usernameCheckRequestRef.current = requestId;
    if (options.step !== "name") {
      setUsernameCheckState("idle");
      return;
    }

    const parsed = UsernameSchema.safeParse(questionState.username);
    if (!parsed.success) {
      setUsernameCheckState(
        questionState.username.length === 0 ? "idle" : "invalid",
      );
      return undefined;
    }

    if (
      entryAuthMode === "authenticated" &&
      canonicalUsername === parsed.data
    ) {
      setQuestionState((current) => ({
        ...current,
        username: parsed.data,
      }));
      setUsernameCheckState("available");
      return undefined;
    }

    setUsernameCheckState("idle");
    const timeout = globalThis.setTimeout(() => {
      void runUsernameAvailabilityCheck(parsed.data, requestId);
    }, USERNAME_AVAILABILITY_DEBOUNCE_MS);

    return () => {
      globalThis.clearTimeout(timeout);
    };
  }, [
    canonicalUsername,
    entryAuthMode,
    options.step,
    questionState.username,
    runUsernameAvailabilityCheck,
    usernameIdentityApplied,
  ]);

  const handleUsernameContinue = async () => {
    const parsed = UsernameSchema.safeParse(questionState.username);
    if (!parsed.success) {
      setUsernameCheckState("invalid");
      return;
    }
    if (usernameCheckState === "error") {
      const requestId = usernameCheckRequestRef.current + 1;
      usernameCheckRequestRef.current = requestId;
      await runUsernameAvailabilityCheck(parsed.data, requestId);
      return;
    }
    if (usernameCheckState !== "available") {
      return;
    }

    if (entryAuthMode === "authenticated" || usernameIdentity.authenticated) {
      if (canonicalUsername === parsed.data) {
        persistDraftPatch({ username: canonicalUsername });
        options.onStepChange("profile");
        return;
      }
      setUsernameCheckState("saving");
      try {
        const result = await usernameIdentity.claimUsername(parsed.data);
        switch (result.status) {
          case "claimed":
            // The successful atomic claim is authoritative even if the
            // preliminary identity read resolves with an older null value.
            setUsernameIdentityApplied(true);
            setCanonicalUsername(result.username);
            setQuestionState((current) => ({
              ...current,
              username: result.username,
            }));
            persistDraftPatch({ username: result.username });
            options.onStepChange("profile");
            return;
          case "preserved":
            setUsernameIdentityApplied(true);
            setCanonicalUsername(result.username);
            setQuestionState((current) => ({
              ...current,
              username: result.username,
            }));
            persistDraftPatch({ username: result.username });
            if (result.username !== parsed.data) {
              toast.info(
                messages.onboarding_v1_username_preserved({
                  username: result.username,
                }),
                { id: "onboarding-v1-username-preserved" },
              );
            }
            options.onStepChange("profile");
            return;
          case "reserved":
          case "unavailable":
            setUsernameCheckState(result.status);
            return;
        }
      } catch {
        setUsernameCheckState("available");
        toast.error(messages.onboarding_v1_username_check_error(), {
          id: "onboarding-v1-username-claim-error",
        });
      }
      return;
    }

    persistDraftPatch({ username: parsed.data });
    options.onStepChange("profile");
  };

  const handleClose = async () => {
    if (
      claimedDraft !== null &&
      !hasOnboardingV1ProvisioningState(claimedDraft)
    ) {
      setIsClosing(true);
      setPageError(null);
      try {
        await accountDataSource.clearClaimedDraft();
        setClaimedDraft(null);
      } catch {
        setPageError("close");
        setIsClosing(false);
        return;
      }
      setIsClosing(false);
    } else {
      clearOnboardingV1Draft(options.draftStorage);
      draftRef.current = null;
      setDraft(null);
    }

    options.onClose();
  };

  const handleClaimedStarterInvalid = useCallback(async () => {
    const currentDraft = claimedDraft;
    if (!currentDraft || hasOnboardingV1ProvisioningState(currentDraft)) {
      return;
    }

    await accountDataSource.clearClaimedDraft();
    const restoredDraft = OnboardingDraftV1Schema.parse({
      interests: currentDraft.interests,
      interestsUnknown: currentDraft.interestsUnknown,
      selectedBlueprintId: null,
      selectedNetwork: "EVM",
      selfDescriptions: currentDraft.selfDescriptions,
      updatedAt: new Date().toISOString(),
      username: currentDraft.username ?? canonicalUsername,
      version: 1,
    });
    queryClient.removeQueries({
      exact: true,
      queryKey:
        queryKeys.platformApi.portfolioBuilderRecommendForOnboardingAnswers({
          interests: [...restoredDraft.interests],
          interestsUnknown: restoredDraft.interestsUnknown,
          selfDescriptions: [...restoredDraft.selfDescriptions],
        }),
    });
    draftRef.current = restoredDraft;
    writeOnboardingV1Draft(restoredDraft, options.draftStorage);
    setDraft(restoredDraft);
    setQuestionState({
      interests: restoredDraft.interests,
      interestsUnknown: restoredDraft.interestsUnknown,
      selfDescriptions: restoredDraft.selfDescriptions,
      username: restoredDraft.username ?? "",
    });
    setClaimedDraft(null);
    setClaimedInCurrentSession(false);
    setClaimedDraftLoadState("loaded");
    options.onStepChange("pick");
  }, [
    accountDataSource,
    canonicalUsername,
    claimedDraft,
    options.draftStorage,
    options.onStepChange,
    queryClient,
  ]);

  const retryPageAction = () => {
    if (pageError === "load") {
      void loadClaimedDraft();
      return;
    }
    if (pageError === "close") {
      void handleClose();
      return;
    }
    if (pageError === "identity") {
      setPageError(null);
      void usernameIdentity.retryCurrentUsername();
    }
  };

  return {
    accountDataSource,
    claimedDraft,
    claimedInCurrentSession,
    draft,
    handleClaimed(nextClaimedDraft: OnboardingV1ClaimedDraft) {
      clearOnboardingV1Draft(options.draftStorage);
      draftRef.current = null;
      setDraft(null);
      setClaimedDraft(nextClaimedDraft);
      setClaimedInCurrentSession(true);
      setClaimedDraftLoadState("loaded");
      options.onStepChange("fund");
    },
    handleClaimedStarterInvalid,
    handleClose,
    handleContinue() {
      switch (options.step) {
        case "hook":
          persistDraftPatch({});
          options.onStepChange(draft?.username ? "profile" : "name");
          break;
        case "profile":
          options.onStepChange("interests");
          break;
        case "interests":
          options.onStepChange("projection");
          break;
        case "pick":
          if (draft?.selectedBlueprintId) {
            options.onStepChange("account");
          }
          break;
        case "projection":
          options.onStepChange("pick");
          break;
        case "name":
        case "account":
        case "fund":
          break;
      }
    },
    handleExplore() {
      clearOnboardingV1Draft(options.draftStorage);
      draftRef.current = null;
      setDraft(null);
      options.onExplore();
    },
    handleInterestToggle(interest: OnboardingInterest) {
      const interests = questionState.interests.includes(interest)
        ? questionState.interests.filter((value) => value !== interest)
        : [...questionState.interests, interest];
      setQuestionState((current) => ({
        ...current,
        interests,
        interestsUnknown: false,
      }));
      persistDraftPatch({ interests, interestsUnknown: false });
    },
    handleInterestsUnknownToggle() {
      const interestsUnknown = !questionState.interestsUnknown;
      setQuestionState((current) => ({
        ...current,
        interests: [],
        interestsUnknown,
      }));
      persistDraftPatch({ interests: [], interestsUnknown });
    },
    handlePickSelect(selectedBlueprintId: string) {
      persistDraftPatch({ selectedBlueprintId });
    },
    handleProfileToggle(description: OnboardingSelfDescription) {
      const selfDescriptions = questionState.selfDescriptions.includes(
        description,
      )
        ? questionState.selfDescriptions.filter(
            (value) => value !== description,
          )
        : [...questionState.selfDescriptions, description];
      setQuestionState((current) => ({
        ...current,
        selfDescriptions,
      }));
      persistDraftPatch({ selfDescriptions });
    },
    handleStarterInvalid() {
      const currentDraft = draftRef.current;
      if (currentDraft) {
        queryClient.removeQueries({
          exact: true,
          queryKey:
            queryKeys.platformApi.portfolioBuilderRecommendForOnboardingAnswers(
              {
                interests: [...currentDraft.interests],
                interestsUnknown: currentDraft.interestsUnknown,
                selfDescriptions: [...currentDraft.selfDescriptions],
              },
            ),
        });
      }
      persistDraftPatch({ selectedBlueprintId: null });
      options.onStepChange("pick");
    },
    handleUsernameChange(value: string) {
      // Invalidate an in-flight availability response before changing the
      // controlled value it belongs to.
      usernameCheckRequestRef.current += 1;
      const username = normalizeUsername(value);
      setQuestionState((current) => ({
        ...current,
        username,
      }));
      setUsernameCheckState("idle");
      persistDraftPatch({
        username: entryAuthMode === "authenticated" ? canonicalUsername : null,
      });
    },
    handleUsernameConflict() {
      setUsernameCheckState("unavailable");
      options.onStepChange("name");
    },
    handleUsernameContinue,
    isClosing,
    shouldHideUsernameStep:
      entryAuthMode === "resolving" ||
      (entryAuthMode === "authenticated" &&
        ((usernameIdentity.authReady && !usernameIdentityApplied) ||
          canonicalUsername !== null)),
    pageError,
    questionState,
    retryPageAction,
    setClaimedDraft,
    selectedBlueprintId: draft?.selectedBlueprintId ?? null,
    usernameCheckState,
  };
}
